import Dexie, { Table } from 'dexie';
import { Project, Section, Block, OutlineOperation, KdpMatterPage, KdpUserPreset, ProjectAsset, SectionAssetLink } from '../../types';
import { ProjectSecret } from '../../types';
import { WritingVersion, SectionContent, LensRun, IdeaBoard, IdeaNode, IdeaEdge, IdeaChapterEdge, IdeaDraftChapter } from '../../types';

export interface RemoteCipherCache {
	id: string; // section_content id
	sectionId: string;
	versionId: string;
	projectId: string;
	envelopeJSON: string; // JSON.stringify({ kid, iv, ct, alg })
	updatedAt: Date;
}

export interface SyncMeta {
	table: 'projects' | 'sections' | 'writingVersions' | 'sectionContents' | 'ideaNodes' | 'ideaEdges' | 'ideaDraftChapters' | 'ideaChapterEdges';
	lastPulledAt?: string; // ISO
	lastPushedAt?: string; // ISO
}

export type SyncEntityType =
	| 'projects'
	| 'writing_versions'
	| 'sections'
	| 'section_contents';

export interface SyncJournal {
	// compound key: `${entityType}:${entityId}`
	key: string;
	entityType: SyncEntityType;
	entityId: string;
	projectId?: string;
	versionId?: string;
	baseRemoteUpdatedAt?: string; // ISO
	baseRemoteHash?: string; // plaintext hash for contents, or structural hash for sections if used
	updatedAt: string; // ISO (local)
}

export type SyncSnapshotKind = 'prePull' | 'prePush' | 'manual' | 'preRestore';

export interface SyncSnapshot {
	id: string;
	projectId?: string;
	kind: SyncSnapshotKind;
	createdAt: string; // ISO
	label?: string;
	// JSON string of a compact snapshot (encrypted in cloud snapshots, plaintext local)
	payloadJSON: string;
}

export type SyncConflictStatus = 'open' | 'resolved' | 'ignored';

export interface UserVault {
  userId: string;     // PK
  swkB64: string;     // SWK en Base64 (AES-256-GCM raw key)
  fetchedAt: string;  // ISO — cuándo se obtuvo de Supabase
}

export interface SyncConflictLocal {
	id: string;
	projectId: string;
	versionId?: string;
	entityType: SyncEntityType;
	entityId: string;
	createdAt: string; // ISO
	updatedAt: string; // ISO
	status: SyncConflictStatus;
	risk: 'HIGH' | 'MEDIUM' | 'LOW';
	reason: string;
	// minimal payload for UI to render a decision
	detailsJSON?: string;
	resolution?: 'preserve_both' | 'keep_local' | 'keep_remote' | 'abort';
}

class WriterLensDB extends Dexie {
	projects!: Table<Project, string>;
	sections!: Table<Section, string>;
  writingVersions!: Table<WritingVersion, string>;
  sectionContents!: Table<SectionContent, string>;
  blocks!: Table<Block, string>;
  projectSecrets!: Table<ProjectSecret, string>;
  sync_meta!: Table<SyncMeta, string>;
  remoteCipherCache!: Table<RemoteCipherCache, string>;
  outlineOps!: Table<OutlineOperation, string>;
  kdp_matter_pages!: Table<KdpMatterPage, string>;
  kdp_user_presets!: Table<KdpUserPreset, string>;
  assets!: Table<ProjectAsset, string>;
  section_assets!: Table<SectionAssetLink, string>;
  sync_journal!: Table<SyncJournal, string>;
  sync_snapshots!: Table<SyncSnapshot, string>;
  sync_conflicts_local!: Table<SyncConflictLocal, string>;
  user_vault!: Table<UserVault, string>;
  lens_runs!: Table<LensRun, string>;
  idea_boards!: Table<IdeaBoard, string>;
  idea_draft_chapters!: Table<IdeaDraftChapter, string>;
  idea_nodes!: Table<IdeaNode, string>;
  idea_edges!: Table<IdeaEdge, string>;
  idea_chapter_edges!: Table<IdeaChapterEdge, string>;

  constructor() {
    super('writers_lens_v2_clean');
    // v6: añade bloques, outlineOps y campos de sincronización extendidos
    this.version(6).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt',
      writingVersions: 'id, projectId, createdAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId', // Compound unique index
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId', // one secret per project (local only)
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt'
    }).upgrade(async (tx) => {
      const nowIso = new Date().toISOString();
      await tx.table('sections').toCollection().modify((section: any) => {
        section.title = section.title ?? section.name ?? 'Sección';
        section.name = section.name ?? section.title;
        section.status = section.status ?? 'todo';
        section.labels = Array.isArray(section.labels) ? section.labels : [];
        section.hidden = section.hidden ?? false;
        section.updatedAt = section.updatedAt ?? nowIso;
      });
    });

    // v7: sync safety (dirty flags, hashes, journal/snapshots/conflicts)
    this.version(7).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt'
    }).upgrade(async (tx) => {
      const nowIso = new Date().toISOString();

      await tx.table('sections').toCollection().modify((s: any) => {
        s.dirty = s.dirty ?? false;
        s.baseRemoteUpdatedAt = s.baseRemoteUpdatedAt ?? undefined;
        s.deletedAt = s.deletedAt ?? undefined;
        s.updatedAt = s.updatedAt ?? nowIso;
      });

      // writingVersions: add updatedAt + kind defaults
      await tx.table('writingVersions').toCollection().modify((v: any) => {
        v.updatedAt = v.updatedAt ?? (v.createdAt ? new Date(v.createdAt).toISOString() : nowIso);
        v.kind = v.kind ?? 'normal';
      });

      // sectionContents: add updatedAt/dirty/baseRemote fields (hash computed lazily)
      await tx.table('sectionContents').toCollection().modify((c: any) => {
        const createdIso = c.createdAt ? new Date(c.createdAt).toISOString() : nowIso;
        c.updatedAt = c.updatedAt ?? createdIso;
        c.dirty = c.dirty ?? false;
        c.contentHash = c.contentHash ?? undefined;
        c.baseRemoteHash = c.baseRemoteHash ?? undefined;
        c.baseRemoteUpdatedAt = c.baseRemoteUpdatedAt ?? undefined;
        c.deletedAt = c.deletedAt ?? undefined;
      });
    });

    // v8: user_vault — local cache for Secret Wrapping Key (SWK)
    this.version(8).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      // nuevo:
      user_vault: 'userId',
    })
    // No upgrade() needed — new empty table, no data migration required.

    this.version(9).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
    })

    this.version(10).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
    })

    this.version(11).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
    }).upgrade(async (tx) => {
      const nowIso = new Date().toISOString();
      await tx.table('assets').toCollection().modify((asset: any) => {
        asset.createdAt = asset.createdAt ?? nowIso;
        asset.updatedAt = asset.updatedAt ?? asset.createdAt ?? nowIso;
        asset.dirty = asset.dirty ?? false;
        asset.deletedAt = asset.deletedAt ?? undefined;
      });
      await tx.table('section_assets').toCollection().modify((link: any) => {
        link.createdAt = link.createdAt ?? nowIso;
        link.updatedAt = link.updatedAt ?? link.createdAt ?? nowIso;
        link.dirty = link.dirty ?? false;
        link.deletedAt = link.deletedAt ?? undefined;
        link.position = typeof link.position === 'number' ? link.position : 0;
      });
    })

    this.version(12).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
      lens_runs: 'id, projectId, versionId, sectionId, lens, scope, createdAt, [projectId+lens+scope], [projectId+sectionId+lens+scope]',
    })

    this.version(13).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
      lens_runs: 'id, projectId, versionId, sectionId, lens, scope, createdAt, [projectId+lens+scope], [projectId+sectionId+lens+scope]',
      idea_boards: 'projectId, updatedAt',
      idea_draft_chapters: 'id, projectId, materializedSectionId, updatedAt',
      idea_nodes: 'id, projectId, type, updatedAt',
      idea_edges: 'id, projectId, sourceNodeId, targetNodeId, type, updatedAt',
    })

    this.version(14).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
      lens_runs: 'id, projectId, versionId, sectionId, lens, scope, createdAt, [projectId+lens+scope], [projectId+sectionId+lens+scope]',
      idea_boards: 'projectId, updatedAt',
      idea_draft_chapters: 'id, projectId, materializedSectionId, updatedAt',
      idea_nodes: 'id, projectId, draftChapterId, type, updatedAt',
      idea_edges: 'id, projectId, sourceNodeId, targetNodeId, type, updatedAt',
    }).upgrade(async (tx) => {
      await tx.table('idea_nodes').toCollection().modify((node: any) => {
        node.draftChapterId = node.draftChapterId ?? undefined;
      });
    })

    // v15: chapter-to-chapter edges for Idea Lens board narrative sequencing
    this.version(15).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
      lens_runs: 'id, projectId, versionId, sectionId, lens, scope, createdAt, [projectId+lens+scope], [projectId+sectionId+lens+scope]',
      idea_boards: 'projectId, updatedAt',
      idea_draft_chapters: 'id, projectId, materializedSectionId, updatedAt',
      idea_nodes: 'id, projectId, draftChapterId, type, updatedAt',
      idea_edges: 'id, projectId, sourceNodeId, targetNodeId, type, updatedAt',
      idea_chapter_edges: 'id, projectId, sourceChapterId, targetChapterId, updatedAt',
    })
    // No upgrade() needed — new empty table, existing data unaffected

    this.version(16).stores({
      projects: 'id',
      sections: 'id, projectId, versionId, parentSectionId, [projectId+versionId], [projectId+versionId+order], updatedAt, dirty',
      writingVersions: 'id, projectId, createdAt, updatedAt',
      sectionContents: 'id, [sectionId+versionId], sectionId, versionId, updatedAt, dirty',
      blocks: 'id, sectionId, index, updatedAt',
      projectSecrets: 'projectId',
      sync_meta: 'table',
      remoteCipherCache: 'id, sectionId, versionId, projectId',
      outlineOps: 'id, projectId, versionId, sectionId, syncState, createdAt',
      kdp_matter_pages: 'id, projectId, placement, anchorSectionId, [projectId+placement], [projectId+placement+order], [projectId+anchorSectionId+placement]',
      kdp_user_presets: 'id, builtIn, updatedAt',
      assets: 'id, projectId, updatedAt, dirty, deletedAt',
      section_assets: 'id, sectionId, assetId, updatedAt, dirty, deletedAt, [sectionId+position]',
      sync_journal: 'key, entityType, entityId, projectId, versionId, updatedAt',
      sync_snapshots: 'id, projectId, kind, createdAt',
      sync_conflicts_local: 'id, projectId, versionId, entityType, entityId, status, createdAt',
      user_vault: 'userId',
      lens_runs: 'id, projectId, versionId, sectionId, lens, scope, createdAt, [projectId+lens+scope], [projectId+sectionId+lens+scope]',
      idea_boards: 'projectId, updatedAt',
      idea_draft_chapters: 'id, projectId, materializedSectionId, updatedAt',
      idea_nodes: 'id, projectId, draftChapterId, type, updatedAt',
      idea_edges: 'id, projectId, sourceNodeId, targetNodeId, type, updatedAt',
      idea_chapter_edges: 'id, projectId, sourceChapterId, targetChapterId, updatedAt',
    }).upgrade(async (tx) => {
      await tx.table('sections').toCollection().modify((section: any) => {
        section.planningState = section.planningState ?? 'normal';
      });
    })

    this.projects = this.table('projects');
    this.sections = this.table('sections');
    this.writingVersions = this.table('writingVersions');
    this.sectionContents = this.table('sectionContents');
    this.blocks = this.table('blocks');
    this.projectSecrets = this.table('projectSecrets');
    this.sync_meta = this.table('sync_meta');
    this.remoteCipherCache = this.table('remoteCipherCache');
    this.outlineOps = this.table('outlineOps');
    this.kdp_matter_pages = this.table('kdp_matter_pages');
    this.kdp_user_presets = this.table('kdp_user_presets');
    this.assets = this.table('assets');
    this.section_assets = this.table('section_assets');
    this.sync_journal = this.table('sync_journal');
    this.sync_snapshots = this.table('sync_snapshots');
    this.sync_conflicts_local = this.table('sync_conflicts_local');
    this.user_vault = this.table('user_vault');
    this.lens_runs = this.table('lens_runs');
    this.idea_boards = this.table('idea_boards');
    this.idea_draft_chapters = this.table('idea_draft_chapters');
    this.idea_nodes = this.table('idea_nodes');
    this.idea_edges = this.table('idea_edges');
    this.idea_chapter_edges = this.table('idea_chapter_edges');
  }
}

export const db = new WriterLensDB();

export type { WriterLensDB };
